import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { locationsApi, corporatesApi, formsApi, usersApi, FRONTEND_BASE_URL } from '../../services/api';
import html2pdf from 'html2pdf.js';
import {
    MapPin,
    Plus,
    Pencil,
    X,
    Search,
    QrCode,
    AlertCircle,
    Eye,
    FileText
} from 'lucide-react';

const LocationsPage = () => {
    const { selectedCorporate } = useOutletContext();
    const [locations, setLocations] = useState([]);
    const [corporates, setCorporates] = useState([]);
    const [formTemplates, setFormTemplates] = useState([]);
    const [smsUsers, setSmsUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [formData, setFormData] = useState({ corporateId: '', name: '', formTemplateIds: [], smsUserIds: [], status: 1 });
    const [saving, setSaving] = useState(false);
    const [showQrModal, setShowQrModal] = useState(false);
    const [selectedQrLocation, setSelectedQrLocation] = useState(null);

    useEffect(() => {
        loadData();
    }, [selectedCorporate]);

    const loadData = async () => {
        if (!selectedCorporate?.id) return;
        setLoading(true);
        setError(null);
        try {
            const [locationsData, corporatesData, formTemplatesData, usersData] = await Promise.all([
                locationsApi.getAll(selectedCorporate?.id),
                corporatesApi.getAll(),
                formsApi.getTemplates(selectedCorporate?.id),
                usersApi.getAll(selectedCorporate?.id)
            ]);
            setLocations(locationsData);
            setCorporates(corporatesData);
            setFormTemplates(formTemplatesData);
            setSmsUsers(usersData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingLocation(null);
        setFormData({
            corporateId: selectedCorporate?.id || '',
            name: '',
            formTemplateIds: [],
            smsUserIds: [],
            status: 1
        });
        setShowModal(true);
    };

    const handleEdit = (location) => {
        setEditingLocation(location);
        setFormData({
            corporateId: location.corporateId,
            name: location.name,
            formTemplateIds: location.formTemplateIds || [],
            smsUserIds: location.smsUserIds || [],
            status: location.status !== undefined && location.status !== null ? location.status : 1
        });
        setShowModal(true);
    };

    const handleDelete = async (location) => {
        if (!window.confirm(`"${location.name}" lokasyonunu silmek istediğinizden emin misiniz?`)) {
            return;
        }

        try {
            await locationsApi.delete(location.id);
            location.status = 0;
            setLocations([...locations]);
        } catch (err) {
            alert('Silme işlemi başarısız: ' + err.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (editingLocation) {
                const updated = await locationsApi.update(editingLocation.id, formData);
                setLocations(locations.map(l => l.id === editingLocation.id ? updated : l));
            } else {
                const created = await locationsApi.create(formData);
                setLocations([...locations, created]);
            }
            setShowModal(false);
        } catch (err) {
            alert('Kaydetme işlemi başarısız: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleShowQr = (location) => {
        setSelectedQrLocation(location);
        setShowQrModal(true);
    };

    const generateQrCodeUrl = (qrCode) => {
        const qrData = `${FRONTEND_BASE_URL()}/form/${qrCode}`;
        return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}`;
    };

    const handleDownloadQr = async () => {
        if (!selectedQrLocation) return;

        try {
            const qrUrl = generateQrCodeUrl(selectedQrLocation.qrCode);
            const response = await fetch(qrUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `QR_${selectedQrLocation.name.replace(/\s+/g, '_')}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('QR kod indirilemedi: ' + err.message);
        }
    };

    const handleDownloadPdf = async () => {
        if (!selectedQrLocation) return;

        try {
            const qrUrl = generateQrCodeUrl(selectedQrLocation.qrCode);
            const selectedCorporateObj = corporates.find(c => String(c.id) === String(selectedQrLocation.corporateId));
            const corporatePhone = selectedCorporateObj?.phoneNumber || '';

            // Fetch and convert QR image to data URL
            const qrResponse = await fetch(qrUrl);
            const qrBlob = await qrResponse.blob();
            const qrDataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(qrBlob);
            });

            let header = '';
            switch (selectedQrLocation.corporateId) {
                case 7:
                    header = `<img class="logo-img" src="${window.location.origin}/sirnak-universitesi.png" alt="ŞIRNAK ÜNİVERSİTESİ" />
                        <div class="header-text">
                            <p class="tc-text" style="padding-bottom: 20px;">${(selectedQrLocation.corporateName).toLocaleUpperCase('tr-TR')}</p>
                        </div>
                        <img class="logo-img" src="${window.location.origin}/sirnak-universitesi.png" alt="ŞIRNAK ÜNİVERSİTESİ" />`;
                    break;
                default:
                    header = `<img class="logo-img" src="${window.location.origin}/saglik-bakanligi-logo.png" alt="TC Sağlık Bakanlığı" />
                        <div class="header-text">
                            <p class="tc-text">T.C. SAĞLIK BAKANLIĞI</p>
                            <p class="tc-text">ŞIRNAK İL SAĞLIK MÜDÜRLÜĞÜ</p>
                            <p class="tc-text">${(selectedQrLocation.corporateName || 'Sağlık Kurumu').toLocaleUpperCase('tr-TR')}</p>
                        </div>
                        <img class="logo-img" src="${window.location.origin}/saglik-bakanligi-logo.png" alt="TC Sağlık Bakanlığı" />`;
            }

            let footer = '';
            switch (selectedQrLocation.corporateId) {
                case 7:
                    footer = `Memnuniyetiniz Bizim İçin Değerlidir`;
                    break;
                default:
                    footer = `Hastalarımızın Memnuniyeti Bizim İçin Değerlidir`;
            }

            let titleSize = '';
            switch (selectedQrLocation.corporateId) {
                case 7:
                    titleSize = '20px';
                    break;
                default:
                    titleSize = '14px';
            }

            const htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>QR Kod - ${selectedQrLocation.name}</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            background: white;
                            width: 100%;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                        }
                        .pdf-wrapper {
                            width: 100%;
                            height: 100%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            padding: 40px 20px;
                        }
                        .container {
                            text-align: center;
                            max-width: 560px;
                            width: 100%;
                            background: white;
                        }
                        .top-header {
                            padding: 16px 20px 10px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 12px;
                        }
                        .logos {
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            width: 100%;
                        }
                        .logo-img {
                            width: 70px;
                            height: 70px;
                            object-fit: contain;
                            flex-shrink: 0;
                        }
                        .header-text {
                            flex: 1;
                            padding: 0 10px;
                        }
                        .tc-text {
                            font-size: ${titleSize};
                            font-weight: bold;
                            color: #333;
                            margin-bottom: 2px;
                        }
                        .banner {
                            background-color: #c0152b;
                            color: white;
                            font-size: 22px;
                            font-weight: bold;
                            letter-spacing: 1px;
                            padding: 2px 20px 22px 20px;
                            text-align: center;
                            transform: translateY(5px);
                        }
                        .body-content {
                            padding: 24px 20px 20px;
                        }
                        .subtitle {
                            font-size: 20px;
                            font-weight: bold;
                            color: #222;
                            margin-bottom: 8px;
                        }
                        .subtitle-bold {
                            font-size: 20px;
                            font-weight: bold;
                            color: #222;
                            margin-bottom: 24px;
                        }
                        .qr-wrapper {
                            text-align: center;
                            margin: 64px 0;
                        }
                        .qr-wrapper img {
                            width: 350px;
                            height: 350px;
                            display: inline-block;
                            border: 1px solid #ccc;
                            padding: 16px;
                        }
                        .thank-you {
                            font-size: 20px;
                            color: #8b1a3a;
                            font-weight: bold;
                            margin-bottom: 8px;
                        }
                        .phone {
                            font-size: 20px;
                            font-weight: bold;
                            color: #222;
                            margin-bottom: 10px;
                        }
                        .footer-text {
                            font-size: 20px;
                            color: #8b1a3a;
                            font-weight: bold;
                        }
                    </style>
                </head>
                <body>
                    <div class="pdf-wrapper">
                        <div class="container">
                            <div class="top-header">
                                <div class="logos">
                                    ${header}
                                </div>
                            </div>

                            <div class="banner">${selectedQrLocation.name}</div>

                            <div class="body-content">
                                <p class="subtitle">Lütfen memnuniyetinizi değerlendirmek için</p>
                                <p class="subtitle-bold">QR kodu telefonunuzla okutunuz.</p>

                                <div class="qr-wrapper">
                                    <img src="${qrDataUrl}" alt="QR Code" />
                                </div>

                                <p class="thank-you">Katkılarınız için teşekkür ederiz</p>
                                <p class="phone">${corporatePhone}</p>
                                <p class="footer-text">${footer}</p>
                            </div>
                        </div>
                    </div>
                </body>
                </html>
            `;

            const opt = {
                margin: 0,
                filename: `QR_${selectedQrLocation.name.replace(/\s+/g, '_')}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, allowTaint: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };

            const element = document.createElement('div');
            element.innerHTML = htmlContent;
            element.style.width = '210mm';
            element.style.height = '280mm';
            element.style.background = 'white';

            html2pdf().set(opt).from(element).save();
        } catch (err) {
            alert('PDF indirilemedi: ' + err.message);
        }
    };

    const getStatusBadge = (status) => {
        return status === 1 ? (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                Aktif
            </span>
        ) : (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                Pasif
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const filteredLocations = locations.filter(location =>
        location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.corporateName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="space-y-6">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Lokasyonlar</h1>
                        <p className="text-gray-500 mt-1">Tüm lokasyonları yönetin</p>
                    </div>
                    <button
                        onClick={handleAdd}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        Yeni Lokasyon
                    </button>
                </div>

                {/* Search */}
                <div className="bg-white rounded-xl shadow-sm border p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Lokasyon veya kurum adı ile ara..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <p className="text-red-700">{error}</p>
                    </div>
                )}

                {/* Locations Table */}
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    {filteredLocations.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            <MapPin className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                            <p>Lokasyon bulunamadı.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Lokasyon
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                            QR Kod
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Durum
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Formlar
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            SMS
                                        </th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Oluşturulma
                                        </th>
                                        <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            İşlemler
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredLocations.map((location) => (
                                        <tr key={location.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <MapPin className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <span className="font-medium text-gray-900">{location.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleShowQr(location)}
                                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="QR Kodu Görüntüle"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">{getStatusBadge(location.status)}</td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {location.formTemplateIds?.length || 0}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {location.smsUserIds?.length || 0}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-gray-500 text-sm">
                                                {formatDate(location.createdAt)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleEdit(location)}
                                                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                                        title="Düzenle"
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {editingLocation ? 'Lokasyon Düzenle' : 'Yeni Lokasyon'}
                            </h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Kurum
                                </label>
                                <select
                                    value={formData.corporateId}
                                    disabled
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                                >
                                    <option value="">Kurum Seçin</option>
                                    {corporates.map((corporate) => (
                                        <option key={corporate.id} value={corporate.id}>
                                            {corporate.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Lokasyon Adı
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Örn: Cerrahi Servis 2. Kat"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Durum
                                </label>
                                <div className="flex gap-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={formData.status === 1}
                                            onChange={() => setFormData({ ...formData, status: 1 })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Aktif</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={formData.status === 0}
                                            onChange={() => setFormData({ ...formData, status: 0 })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Pasif</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Form Şablonları
                                </label>
                                <div className="space-y-2 border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                                    {formTemplates.length === 0 ? (
                                        <p className="text-sm text-gray-500">Form şablonu bulunamadı</p>
                                    ) : (
                                        formTemplates.map((template) => (
                                            <label key={template.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.formTemplateIds.includes(template.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFormData({
                                                                ...formData,
                                                                formTemplateIds: [...formData.formTemplateIds, template.id]
                                                            });
                                                        } else {
                                                            setFormData({
                                                                ...formData,
                                                                formTemplateIds: formData.formTemplateIds.filter(id => id !== template.id)
                                                            });
                                                        }
                                                    }}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <div className="flex flex-col">
                                                    <span className="text-sm text-gray-700">{template.title}</span>
                                                    <span className="text-xs text-gray-500">(Personel Giriş: {template.isPersonelLoginRequired ? 'Gerekli' : 'Gerekli Değil'})</span>
                                                </div>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Bu Lokasyon İçin SMS Bildirim Kullanıcıları
                                </label>
                                <div className="space-y-2 border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                                    {smsUsers.length === 0 ? (
                                        <p className="text-sm text-gray-500">SMS kullanıcısı bulunamadı</p>
                                    ) : (
                                        smsUsers.map((user) => (
                                            <label key={user.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.smsUserIds.includes(user.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setFormData({
                                                                ...formData,
                                                                smsUserIds: [...formData.smsUserIds, user.id]
                                                            });
                                                        } else {
                                                            setFormData({
                                                                ...formData,
                                                                smsUserIds: formData.smsUserIds.filter(id => id !== user.id)
                                                            });
                                                        }
                                                    }}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <div className="flex flex-col">
                                                    <span className="text-sm text-gray-700">{user.fullName}</span>
                                                    <span className="text-xs text-gray-500">{user.phoneNumber || 'Telefon numarası yok'}</span>
                                                </div>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                    {saving ? 'Kaydediliyor...' : 'Kaydet'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* QR Code Modal */}
            {showQrModal && selectedQrLocation && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">
                                QR Kod
                            </h3>
                            <button
                                onClick={() => {
                                    setShowQrModal(false);
                                    setSelectedQrLocation(null);
                                }}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 flex flex-col items-center">
                            <h4 className="text-lg font-medium text-gray-900 mb-1">
                                {selectedQrLocation.name}
                            </h4>
                            <p className="text-sm text-gray-500 mb-4">
                                {selectedQrLocation.corporateName}
                            </p>

                            <div className="bg-white p-4 rounded-lg border shadow-sm mb-4">
                                <img
                                    src={generateQrCodeUrl(selectedQrLocation.qrCode)}
                                    alt="QR Code"
                                    className="w-64 h-64"
                                />
                            </div>

                            <p className="font-mono text-sm text-gray-600 bg-gray-100 px-4 py-2 rounded-lg mb-6">
                                {selectedQrLocation.qrCode}
                            </p>

                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={handleDownloadQr}
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <QrCode className="w-4 h-4" />
                                    QR İndir
                                </button>
                                <button
                                    onClick={handleDownloadPdf}
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                >
                                    <FileText className="w-4 h-4" />
                                    PDF İndir
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationsPage;
